import { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { stopImpersonation, startImpersonation, type StaffViewRole } from '../services/staff'
import { scrubVendorText } from '../utils/scrubVendorText'

export interface ImpersonationInfo {
  orgId: string
  orgName: string
  clinicId?: string | null
  clinicName?: string | null
  role: StaffViewRole
  mode: 'view' | 'edit'
  impUntil: number | null
}

interface ImpersonationBannerProps {
  info: ImpersonationInfo
  /** Refresh the ID token (to pick up new claims) and reload. */
  onReloadSession: () => void
}

const ROLE_LABEL: Record<StaffViewRole, string> = {
  admin: 'Admin', manager: 'Manager', user: 'Staff',
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'expired'
  const mins = Math.floor(ms / 60000)
  const secs = Math.floor((ms % 60000) / 1000)
  return `${mins}:${String(secs).padStart(2, '0')}`
}

/**
 * Pinned banner shown while a staff operator is inside a customer tenant.
 * Blue = read-only view, amber = editing. You can flip view↔edit in place
 * (switching to edit asks for a reason) without leaving the practice. Counts
 * down the 60-min session and auto-exits cleanly on expiry.
 */
export default function ImpersonationBanner({ info, onReloadSession }: ImpersonationBannerProps) {
  const [now, setNow] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)
  const [askingReason, setAskingReason] = useState(false)
  const [switchReason, setSwitchReason] = useState('')
  const exitedRef = useRef(false)
  const editing = info.mode === 'edit'

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const remaining = info.impUntil ? info.impUntil - now : null

  const handleExit = useCallback(async (expired = false) => {
    if (exitedRef.current) return
    exitedRef.current = true
    setBusy(true)
    try {
      await stopImpersonation()
    } catch (err) {
      Sentry.captureException(err)
      // Exit anyway — worst case is an expired session the rules already deny.
    }
    if (expired) toast.info('Your session expired.')
    onReloadSession()
  }, [onReloadSession])

  // Auto-exit the moment the session expires → clean return to the console.
  useEffect(() => {
    if (remaining !== null && remaining <= 0 && !exitedRef.current) {
      const t = setTimeout(() => handleExit(true), 0)
      return () => clearTimeout(t)
    }
  }, [remaining, handleExit])

  // Flip the current session's mode in place (same practice/clinic/role).
  async function switchMode(mode: 'view' | 'edit', reason: string) {
    setBusy(true)
    try {
      await startImpersonation(info.orgId, reason, {
        clinicId: info.clinicId ?? null, role: info.role, mode,
      })
      onReloadSession() // refresh token → reload into the new mode
    } catch (err) {
      Sentry.captureException(err)
      toast.error(err instanceof Error ? scrubVendorText(err.message) : 'Could not switch mode')
      setBusy(false)
    }
  }

  function confirmSwitchToEdit() {
    if (switchReason.trim().length < 3) return
    switchMode('edit', switchReason.trim())
  }

  return (
    <div className={`imp-banner ${editing ? 'imp-banner--edit' : 'imp-banner--view'}`}>
      <div className="imp-banner-main">
        <span className="imp-banner-dot" aria-hidden />
        <span className="imp-banner-text">
          {editing ? 'Editing' : 'Viewing'} <strong>{info.orgName}</strong>
          {info.clinicName
            ? <> / <strong>{info.clinicName}</strong></>
            : <> <span className="imp-banner-scope">(all clinics)</span></>}
          {' '}as <strong>{ROLE_LABEL[info.role]}</strong>
          {' '}— {editing ? <strong>changes are live &amp; logged</strong> : <strong>read-only</strong>}
        </span>
        {remaining !== null && (
          <span className={`imp-banner-timer${remaining < 5 * 60000 ? ' imp-banner-timer-low' : ''}`}>
            {formatRemaining(remaining)} left
          </span>
        )}
        <div className="imp-banner-actions">
          {editing ? (
            <button
              className="imp-banner-switch"
              onClick={() => switchMode('view', 'Returning to read-only')}
              disabled={busy}
            >
              Switch to read-only
            </button>
          ) : (
            <button
              className="imp-banner-switch"
              onClick={() => { setAskingReason(true); setSwitchReason('') }}
              disabled={busy}
            >
              Switch to editing
            </button>
          )}
          <button className="imp-banner-exit" onClick={() => handleExit()} disabled={busy}>
            Exit
          </button>
        </div>
      </div>

      {askingReason && !editing && (
        <div className="imp-banner-reason">
          <span className="imp-banner-reason-label">Why are you editing {info.clinicName ?? info.orgName}?</span>
          <input
            autoFocus
            className="imp-banner-reason-input"
            placeholder="e.g. Fixing clinic timezone at customer request — ticket #214"
            value={switchReason}
            onChange={(e) => setSwitchReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmSwitchToEdit() }}
          />
          <button className="imp-banner-switch" onClick={confirmSwitchToEdit} disabled={busy || switchReason.trim().length < 3}>
            {busy ? 'Switching…' : 'Start editing'}
          </button>
          <button className="imp-banner-exit" onClick={() => setAskingReason(false)} disabled={busy}>
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}
